import { useEffect, useMemo, useRef, useState } from 'react'
import { prepacksApi } from '@/services/api'
import { canonicalAiLeg } from './prepackCardModel'

// Legs arrive one at a time; wait for the selection to settle.
export const AUTO_SUGGEST_DELAY_MS = 1000

// The title styles the service writes in (#543), with the label shown on a
// suggestion and on its quick action.
export const TITLE_STYLE_LABELS = {
  wordplay: 'Wordplay',
  saying: 'Saying',
  local: 'Local',
  story: 'Story',
  descriptive: 'Descriptive',
}

export function copySuggestionLegKey(legs) {
  return JSON.stringify(Array.isArray(legs) ? legs.map(canonicalAiLeg) : [])
}

// Auto-suggestions may only overwrite empty or AI-written fields.
export function isAutoWritable(field) {
  return !String(field?.value ?? '').trim() || field?.source === 'ai'
}

// AI endpoints answer { error: { message } }; prepack validation answers { error: '...' }.
function errorMessage(err) {
  const error = err?.response?.data?.error
  return (typeof error === 'string' ? error : error?.message) || err?.message || 'AI title generation failed.'
}

function fieldsSignature(fields) {
  return JSON.stringify(Object.keys(fields).sort().map((key) => [key, fields[key]?.value ?? '', fields[key]?.source ?? 'operator']))
}

// taglines[i] and styles[i] belong to titles[i]; a tagline is empty when it
// failed server checks, a style when the model named none of the five.
function normalizeResults(res) {
  const titles = Array.isArray(res?.titles) && res.titles.length > 0
    ? res.titles
    : (res?.title ? [res.title] : [])
  const taglines = Array.isArray(res?.taglines) ? res.taglines : (res?.tagline ? [res.tagline] : [])
  const styles = Array.isArray(res?.styles) ? res.styles : (res?.style ? [res.style] : [])
  return titles.map((title, index) => ({ title, tagline: taglines[index] || '', style: styles[index] || '' }))
    .filter((result) => result.title)
    .map((result) => ({
      ...result,
      text: result.tagline ? `${result.title} — ${result.tagline}` : result.title,
      label: TITLE_STYLE_LABELS[result.style] || '',
    }))
}

/**
 * AI copy suggestions for the prepack builder. `fields` maps a key ('title',
 * optionally 'tagline') to { value, source, onChange(value, source) }; a
 * tagline field requests matched pairs. Automatic requests also pre-fill the
 * top result; stale responses are dropped.
 */
export function usePrepackCopySuggestions({
  orgSlug,
  regulation,
  legs = [],
  fields,
  disabled = false,
  manualEnabled = true,
  autoSuggest = false,
  skipLegKey = null,
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState([])
  const activeGenId = useRef(0)
  const revision = useRef(0)
  const mounted = useRef(true)
  const controller = useRef(null)
  const fieldsRef = useRef(fields)
  const expectedSignature = useRef(fieldsSignature(fields))
  const lastAutoScope = useRef(null)
  const autoTimer = useRef(null)
  const autoInFlight = useRef(false)
  const scopeRef = useRef('')

  const canonicalLegs = useMemo(() => (Array.isArray(legs) ? legs.map(canonicalAiLeg) : []), [legs])
  const legKey = useMemo(() => JSON.stringify(canonicalLegs), [canonicalLegs])
  const scopeIdentity = `${orgSlug || ''}|${regulation || ''}|${legKey}`
  const signature = fieldsSignature(fields)
  // One leg is enough: a straight gets titles like any pack.
  const canGenerate = Boolean(orgSlug && canonicalLegs.length >= 1)

  fieldsRef.current = fields
  scopeRef.current = scopeIdentity

  const invalidate = () => {
    activeGenId.current += 1
    controller.current?.abort()
    controller.current = null
    setBusy(false)
  }

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      controller.current?.abort()
    }
  }, [])

  useEffect(() => {
    invalidate()
    setError('')
    setResults([])
  }, [scopeIdentity])

  useEffect(() => {
    if (disabled) invalidate()
  }, [disabled])

  // AI switched off mid-request: drop the response.
  useEffect(() => {
    if (!manualEnabled || (!autoSuggest && autoInFlight.current)) invalidate()
  }, [manualEnabled, autoSuggest])

  // Controlled values can change from a parent without an input event.
  useEffect(() => {
    if (expectedSignature.current === signature) return
    expectedSignature.current = signature
    revision.current += 1
    invalidate()
  }, [signature])

  const applyValues = (values) => {
    const current = fieldsRef.current
    const keys = Object.keys(values).filter((key) => current[key] && values[key])
    if (keys.length === 0) return
    const next = { ...current }
    for (const key of keys) next[key] = { ...current[key], value: values[key], source: 'ai' }
    expectedSignature.current = fieldsSignature(next)
    for (const key of keys) current[key].onChange?.(values[key], 'ai')
  }

  // `style` asks for one title style; without it the service mixes styles.
  const request = async (direction, { auto = false, style = '' } = {}) => {
    if (!canGenerate || disabled || (!auto && (busy || !manualEnabled))) return
    // A manual request settles this scope; the pending automatic one must not replace it.
    if (!auto) {
      clearTimeout(autoTimer.current)
      lastAutoScope.current = scopeIdentity
    }
    autoInFlight.current = auto

    const genId = ++activeGenId.current
    const requestRevision = revision.current
    const requestScope = scopeIdentity
    const requestController = new AbortController()
    controller.current?.abort()
    controller.current = requestController
    setBusy(true)
    setError('')

    const payload = {
      regulation: regulation || canonicalLegs[0]?.regulation,
      legs: canonicalLegs,
      count: 5,
    }
    if (direction) payload.direction = direction
    if (style) payload.title_style = style
    if (fieldsRef.current.tagline) payload.include_tagline = true

    const stale = () => (
      !mounted.current
      || activeGenId.current !== genId
      || revision.current !== requestRevision
      || scopeRef.current !== requestScope
      || requestController.signal.aborted
    )

    try {
      const res = await prepacksApi.generateTitle(orgSlug, payload, { signal: requestController.signal })
      if (stale()) return
      const next = normalizeResults(res)
      if (next.length === 0) {
        if (!auto) setError('No title suggestion returned.')
        return
      }
      setResults(next)
      if (auto) {
        const top = next[0]
        const values = {}
        for (const [key, field] of Object.entries(fieldsRef.current)) {
          if (isAutoWritable(field) && top[key]) values[key] = top[key]
        }
        applyValues(values)
      }
    } catch (err) {
      // Automatic requests fail silently.
      if (!auto && !stale()) setError(errorMessage(err))
    } finally {
      if (controller.current === requestController) controller.current = null
      if (mounted.current && activeGenId.current === genId) setBusy(false)
    }
  }

  // Once per settled leg scope; never for a saved pack's own legs.
  useEffect(() => {
    if (!autoSuggest || disabled || !canGenerate) return undefined
    if (skipLegKey !== null && legKey === skipLegKey) return undefined
    if (lastAutoScope.current === scopeIdentity) return undefined
    autoTimer.current = setTimeout(() => {
      lastAutoScope.current = scopeIdentity
      if (!Object.values(fieldsRef.current).some(isAutoWritable)) return
      request('', { auto: true })
    }, AUTO_SUGGEST_DELAY_MS)
    return () => clearTimeout(autoTimer.current)
    // request reads refs for everything that changes without a new scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSuggest, disabled, canGenerate, legKey, skipLegKey, scopeIdentity])

  const applyResult = (result) => {
    if (disabled || !result) return
    const values = {}
    for (const key of Object.keys(fieldsRef.current)) {
      if (result[key]) values[key] = result[key]
    }
    applyValues(values)
  }

  const noteManualEdit = (key, value) => {
    const next = { ...fieldsRef.current, [key]: { ...fieldsRef.current[key], value, source: 'operator' } }
    expectedSignature.current = fieldsSignature(next)
    revision.current += 1
    invalidate()
    setError('')
    fieldsRef.current[key]?.onChange?.(value, 'operator')
  }

  return { busy, error, results, canGenerate, request, applyResult, noteManualEdit }
}

export default usePrepackCopySuggestions
